/**
 * Tenant accent colour (UAT DEF-180). One admin-picked colour drives the design
 * system's accent tokens (styles/tokens.css) in every theme: bento, classic and
 * the presets, light and dark. The shades are derived with contrast guards so
 * accent buttons keep readable white text and accent text stays legible on
 * cards — a very light or very dark pick is nudged until it passes.
 *
 * The CSS is cached in localStorage so index.html can apply it before first
 * paint (no flash of the default purple on reload).
 */

type RGB = [number, number, number];

const HEX = /^#[0-9a-fA-F]{6}$/;
export const ACCENT_STYLE_ID = 'tenant-accent';
export const ACCENT_LS_KEY = 'ilearn_accent_css';

export const isHexColor = (v: unknown): v is string => typeof v === 'string' && HEX.test(v);

const WHITE: RGB = [255, 255, 255];
const BLACK: RGB = [0, 0, 0];
const CARD_LIGHT: RGB = [255, 255, 255]; // --bg-card (light)
const CARD_DARK: RGB = [23, 24, 29];     // --bg-card (dark)

const toRgb = (hex: string): RGB => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as RGB;
const toHex = (c: RGB) =>
  '#' + c.map((x) => Math.round(Math.min(255, Math.max(0, x))).toString(16).padStart(2, '0')).join('').toUpperCase();
/** `t` is the share of `b` in the result. */
const mix = (a: RGB, b: RGB, t: number): RGB => a.map((x, i) => x + (b[i] - x) * t) as RGB;
const luminance = (c: RGB) => {
  const [r, g, b] = c.map((x) => {
    const s = x / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const contrast = (a: RGB | string, b: RGB | string) => {
  const la = luminance(typeof a === 'string' ? toRgb(a) : a);
  const lb = luminance(typeof b === 'string' ? toRgb(b) : b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
const round = (c: RGB): RGB => c.map((x) => Math.round(Math.min(255, Math.max(0, x)))) as RGB;
/** Move `c` toward `to` in small steps until `ok` holds (tested on the rounded colour that will be emitted). */
const shiftUntil = (c: RGB, to: RGB, ok: (x: RGB) => boolean): RGB => {
  for (let t = 0; t <= 1; t += 0.02) {
    const x = round(mix(c, to, t));
    if (ok(x)) return x;
  }
  return to;
};
const rgba = (c: RGB, a: number) => `rgba(${c.map(Math.round).join(', ')}, ${a})`;

export interface AccentTokens {
  light: Record<string, string>;
  dark: Record<string, string>;
}

export const accentTokens = (hex: string): AccentTokens => {
  const base = toRgb(hex);

  // Light: white text on the accent (buttons) needs 4.5:1; accent text on cards 7:1.
  const accent = shiftUntil(base, BLACK, (x) => contrast(x, WHITE) >= 4.5);
  const hero = mix(accent, BLACK, 0.12);
  const ink = shiftUntil(accent, BLACK, (x) => contrast(x, CARD_LIGHT) >= 7);
  const light = {
    '--accent': toHex(accent),
    '--accent-hero': toHex(hero),
    '--accent-ink': toHex(ink),
    '--accent-ink-hover': toHex(mix(ink, BLACK, 0.35)),
    '--accent-tint': toHex(mix(WHITE, accent, 0.14)),
    '--accent-tint-soft': toHex(mix(WHITE, accent, 0.07)),
    '--focus-ring': toHex(accent),
  };

  // Dark: start a little lighter (as the default palette does), keep white text
  // on the accent at 3:1 and the accent itself visible on dark cards at 3:1.
  let dAccent = mix(base, WHITE, 0.15);
  if (contrast(dAccent, WHITE) < 3) dAccent = shiftUntil(dAccent, BLACK, (x) => contrast(x, WHITE) >= 3);
  if (contrast(dAccent, CARD_DARK) < 3) dAccent = shiftUntil(dAccent, WHITE, (x) => contrast(x, CARD_DARK) >= 3);
  const dInk = shiftUntil(dAccent, WHITE, (x) => contrast(x, CARD_DARK) >= 7);
  const dark = {
    '--accent': toHex(dAccent),
    '--accent-hero': toHex(hero),
    '--accent-ink': toHex(dInk),
    '--accent-ink-hover': toHex(mix(dInk, WHITE, 0.4)),
    '--accent-tint': rgba(dAccent, 0.18),
    '--accent-tint-soft': rgba(dAccent, 0.1),
    '--focus-ring': toHex(dInk),
  };
  return { light, dark };
};

/** !important beats every theme/preset block in tokens.css; the dark rule is more specific than the light one. */
export const accentCss = (hex: string): string => {
  const { light, dark } = accentTokens(hex);
  const decl = (t: Record<string, string>) => Object.entries(t).map(([k, v]) => `${k}: ${v} !important;`).join(' ');
  return `:root { ${decl(light)} }\n:root[data-theme="dark"] { ${decl(dark)} }`;
};

/** Apply (or, with no valid colour, remove) the tenant accent for the whole app. */
export const applyTenantAccent = (hex: string | null | undefined) => {
  const css = isHexColor(hex) ? accentCss(hex) : null;
  try {
    if (css) localStorage.setItem(ACCENT_LS_KEY, css);
    else localStorage.removeItem(ACCENT_LS_KEY);
  } catch { /* storage blocked — the style tag alone still works */ }
  let el = document.getElementById(ACCENT_STYLE_ID) as HTMLStyleElement | null;
  if (!css) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = ACCENT_STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = css;
};
